// src/store/slices/programSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { program } from "../../types";

// Initial state for the program slice
const initialState: program.ProgramStateType = {
  programRecords: {
    data: null,
    isLoading: false,
    error: null,
  },
  uploadProgramRecords: {
    data: null,
    isLoading: false,
    error: null,
  },
  programDetails: {
    data: null,
    isLoading: false,
    error: null,
  },
  benefitAction: {
    data: null,
    isLoading: false,
    error: null,
  },
};

// Define the program slice
const programSlice = createSlice({
  name: "program",
  initialState,
  reducers: {
    // get program records
    getProgramRecordsRequest: (
      state: program.ProgramStateType,
      action: PayloadAction<any>,
    ) => {
      state.programRecords.isLoading = true;
      state.programRecords.data = null;
      state.programRecords.error = null;
    },
    getProgramRecordsSuccess: (
      state: program.ProgramStateType,
      action: PayloadAction<any>,
    ) => {
      state.programRecords.isLoading = false;
      state.programRecords.data = action.payload;
      state.programRecords.error = null;
    },
    getProgramRecordsError: (
      state: program.ProgramStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.programRecords.isLoading = false;
      state.programRecords.data = null;
      state.programRecords.error = error;
    },

    // upload program records
    uploadProgramRecordsRequest: (
      state: program.ProgramStateType,
      action: PayloadAction<any>,
    ) => {
      state.uploadProgramRecords.isLoading = true;
      state.uploadProgramRecords.data = null;
      state.uploadProgramRecords.error = null;
    },
    uploadProgramRecordsSuccess: (
      state: program.ProgramStateType,
      action: PayloadAction<any>,
    ) => {
      state.uploadProgramRecords.isLoading = false;
      state.uploadProgramRecords.data = action.payload;
      state.uploadProgramRecords.error = null;
    },
    uploadProgramRecordsError: (
      state: program.ProgramStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.uploadProgramRecords.isLoading = false;
      state.uploadProgramRecords.data = null;
      state.uploadProgramRecords.error = error;
    },

    // program details (GoodFlip Care)
    getProgramDetailsGfcRequest: (
      state: program.ProgramStateType,
      action: PayloadAction<any>,
    ) => {
      state.programDetails.isLoading = true;
      // Previously fetched details are kept so a refetch (e.g. after a
      // document upload) refreshes in place instead of blanking the screen.
      state.programDetails.error = null;
    },
    getProgramDetailsGfcSuccess: (
      state: program.ProgramStateType,
      action: PayloadAction<any>,
    ) => {
      state.programDetails.isLoading = false;
      state.programDetails.data = action.payload;
      state.programDetails.error = null;
    },
    getProgramDetailsGfcError: (
      state: program.ProgramStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.programDetails.isLoading = false;
      state.programDetails.data = null;
      state.programDetails.error = error;
    },

    // claim a milestone benefit
    benefitActionRequest: (
      state: program.ProgramStateType,
      action: PayloadAction<any>,
    ) => {
      state.benefitAction.isLoading = true;
      state.benefitAction.data = null;
      state.benefitAction.error = null;
    },
    benefitActionSuccess: (
      state: program.ProgramStateType,
      action: PayloadAction<any>,
    ) => {
      state.benefitAction.isLoading = false;
      state.benefitAction.data = action.payload;
      state.benefitAction.error = null;
    },
    benefitActionError: (
      state: program.ProgramStateType,
      { payload: error }: PayloadAction<string>,
    ) => {
      state.benefitAction.isLoading = false;
      state.benefitAction.data = null;
      state.benefitAction.error = error;
    },
  },
});

// Export actions and reducer
export const {
  // get program records
  getProgramRecordsRequest,
  getProgramRecordsSuccess,
  getProgramRecordsError,

  // upload program record
  uploadProgramRecordsRequest,
  uploadProgramRecordsSuccess,
  uploadProgramRecordsError,

  // program details (GoodFlip Care)
  getProgramDetailsGfcRequest,
  getProgramDetailsGfcSuccess,
  getProgramDetailsGfcError,

  // claim a milestone benefit
  benefitActionRequest,
  benefitActionSuccess,
  benefitActionError,
} = programSlice.actions;

export default programSlice.reducer;
